import { useState, useRef } from 'react';
import Button from '../../../components/base/Button';

export default function ServicesSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const services = [
    {
      title: '24/7 Pannendienst',
      description:
        'Pannendienst in Berlin bei Fahrzeugausfall, Batterieproblemen oder technischen Defekten. Wir organisieren Verladung und Abtransport abhängig von Verkehr und Einsatzlage.'
    },
    {
      title: 'Unfallbergung',
      description:
        'Unfallbergung in Berlin nach Kollision oder technischem Schaden. Der Einsatz umfasst Absicherung, Verladung und strukturierten Abtransport des Fahrzeugs.'
    },
    {
      title: 'Falschparker Privat',
      description:
        'Abschleppen von Falschparkern auf Privatgrund in Berlin, etwa bei blockierten Einfahrten oder Parkflächen. Die Abwicklung erfolgt mit nachvollziehbarer Dokumentation.'
    },
    {
      title: 'Starthilfe vor Ort',
      description:
        'Unterstützung bei leerer Batterie oder kleineren technischen Problemen vor Ort. Wenn erforderlich, erfolgt die weitere Verladung und der Transport.'
    },
    {
      title: 'Hof & Tiefgarage',
      description:
        'Abschleppen und Bergung aus Höfen, Tiefgaragen oder engen Zufahrten in Berlin. Geeignet für komplexe Einsatzorte mit eingeschränkter Rangierfläche.'
    },
    {
      title: 'Motorrad & Roller',
      description:
        'Transport von Motorrädern und Rollern in Berlin nach Panne oder Unfall. Fachgerechte Sicherung und Abtransport zum gewünschten Zielort.'
    }
  ];

  const goToSlide = (index: number) => {
    const normalizedIndex = (index + services.length) % services.length;
    if (normalizedIndex === currentIndex) return;

    setCurrentIndex(normalizedIndex);

    const el = mobileScrollRef.current;
    if (el) {
      el.scrollTo({
        left: normalizedIndex * el.clientWidth,
        behavior: 'smooth',
      });
    }
  };

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    goToSlide(currentIndex + 1);
    setTimeout(() => setIsTransitioning(false), 350);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    goToSlide(currentIndex - 1);
    setTimeout(() => setIsTransitioning(false), 350);
  };


  // Mobile scroll handler for snap carousel
  const handleMobileScroll = () => {
    const el = mobileScrollRef.current;
    if (!el) return;
    const newIndex = Math.round(el.scrollLeft / el.clientWidth);
    if (newIndex !== currentIndex) setCurrentIndex(newIndex);
  };


  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-24 bg-[#061225] relative overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto mb-12 lg:mb-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <p
                className="text-xs tracking-[0.22em] uppercase mb-3 text-sky-300"
              >
                Leistungen &amp; Einsatzbereiche
              </p>
              <h2
                className="text-3xl lg:text-4xl font-semibold mb-3 lg:mb-0 text-white"
              >
                Abschlepp- &amp; Bergungsdienst für Berlin
              </h2>
            </div>
            <p
              className="text-sm lg:text-base text-white/80 max-w-xl lg:text-right"
            >
              Als privater Abschleppdienst in Berlin übernehmen wir Pannendienst, Unfallbergung sowie das Abschleppen von Falschparkern auf Privatgrund. Einsätze erfolgen in allen Berliner Bezirken und im Umland – transparent koordiniert und abhängig von Verkehr und Einsatzlage.
            </p>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto">

          {/* Card Container in eigenem Panel */}
          <div className="w-full px-0 lg:px-4">
            <div
              ref={mobileScrollRef}
              className="w-full flex overflow-x-auto lg:grid lg:grid-cols-3 lg:overflow-visible scroll-smooth snap-x snap-mandatory lg:snap-none gap-6 px-5 md:px-6 py-6 rounded-3xl bg-white/[0.03] backdrop-blur-2xl border border-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.35)] [scrollbar-width:none] [-ms-overflow-style:none] services-scroll"
              onScroll={handleMobileScroll}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {services.map((svc) => (
                <div key={svc.title} className="group shrink-0 w-[84%] sm:w-[72%] md:w-[46%] lg:w-auto xl:w-auto snap-center lg:snap-start">
                  <div className="relative overflow-hidden rounded-[1.9rem] border border-white/10 bg-white/[0.04] p-6 md:p-7 backdrop-blur-2xl shadow-[0_22px_70px_rgba(0,0,0,0.45)] transition-all duration-300 min-h-[300px] md:min-h-[320px] flex flex-col justify-between hover:-translate-y-1 hover:border-sky-400/30 hover:bg-white/[0.06] hover:shadow-[0_30px_90px_rgba(2,132,199,0.16)] lg:w-full">
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),transparent_40%,transparent)]" />

                    <div className="relative z-10 flex flex-col h-full">
                      <div className="mb-6 flex items-center gap-3">
                        <div className="h-3 w-3 rounded-full bg-sky-400 shadow-[0_0_18px_rgba(56,189,248,0.7)]" />
                        <div className="h-px flex-1 bg-gradient-to-r from-sky-400/30 to-transparent" />
                      </div>

                      <h3 className="text-base md:text-lg lg:text-xl font-semibold tracking-[0.08em] uppercase text-white leading-snug">
                        {svc.title}
                      </h3>

                      <p className="mt-4 text-slate-300 leading-relaxed text-sm md:text-base">
                        {svc.description}
                      </p>

                      <div className="mt-6 inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-sky-300">
                        <span>Schneller Einsatz</span>
                        <span className="h-px w-8 bg-sky-400/30" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>


          {/* Dots Navigation mit Pfeilen */}
          <div className="flex flex-col items-center justify-center mt-8 lg:mt-12 gap-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={prevSlide}
                disabled={isTransitioning}
                aria-label="Vorherige Leistung"
                className="w-8 h-8 flex items-center justify-center rounded-full border border-sky-400/30 bg-white/[0.04] hover:bg-white/[0.08] transition-all duration-200 cursor-pointer disabled:opacity-40"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-4 w-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>

              <div className="flex items-center gap-2">
                {services.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToSlide(index)}
                    disabled={isTransitioning}
                    aria-label={`Zu Leistung ${index + 1} wechseln`}
                    aria-current={currentIndex === index ? 'true' : undefined}
                    className="w-11 h-11 flex items-center justify-center cursor-pointer disabled:opacity-50"
                  >
                    <span
                      className={`block h-2.5 rounded-full bg-sky-400 transition-all duration-300 ${
                        currentIndex === index ? 'w-7 opacity-100' : 'w-2.5 opacity-40'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={nextSlide}
                disabled={isTransitioning}
                aria-label="Nächste Leistung"
                className="w-8 h-8 flex items-center justify-center rounded-full border border-sky-400/30 bg-white/[0.04] hover:bg-white/[0.08] transition-all duration-200 cursor-pointer disabled:opacity-40"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-4 w-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col justify-center items-center gap-4 mt-16">
          <Button
            href="tel:+4915235750000"
            size="lg"
            className="w-full sm:w-auto text-[0.72rem] sm:text-sm md:text-base px-6 sm:px-8 py-3.5 rounded-full bg-gradient-to-r from-sky-400 to-blue-600 backdrop-blur-xl !text-white border border-sky-300/30 shadow-[0_18px_40px_rgba(37,99,235,0.35)] tracking-[0.16em] sm:tracking-[0.22em] uppercase hover:from-sky-300 hover:to-blue-500 hover:shadow-[0_22px_55px_rgba(37,99,235,0.5)]"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="mr-3 h-5 w-5 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.35 1.78.68 2.62a2 2 0 0 1-.45 2.11L8.03 9.97a16 16 0 0 0 6 6l1.52-1.26a2 2 0 0 1 2.11-.45c.84.33 1.72.56 2.62.68A2 2 0 0 1 22 16.92z" />
            </svg>
            Jetzt anrufen
          </Button>
          <Button
            href="https://wa.me/4915235750000"
            size="lg"
            className="w-full sm:w-auto text-[0.72rem] sm:text-sm md:text-base px-6 sm:px-8 py-3.5 rounded-full bg-black/40 backdrop-blur-2xl text-white border border-white/20 shadow-[0_18px_40px_rgba(0,0,0,0.45)] tracking-[0.16em] sm:tracking-[0.22em] uppercase hover:bg-black/60 hover:shadow-[0_22px_55px_rgba(0,0,0,0.65)]"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="mr-3 h-5 w-5 shrink-0"
              fill="currentColor"
            >
              <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.52 0 .18 5.34.18 11.88c0 2.1.54 4.14 1.56 5.94L0 24l6.36-1.68a11.8 11.8 0 0 0 5.7 1.44h.06c6.54 0 11.88-5.34 11.88-11.88 0-3.18-1.26-6.18-3.48-8.4ZM12.12 21.72h-.06a9.78 9.78 0 0 1-4.98-1.38l-.36-.18-3.78 1.02 1.02-3.66-.24-.36a9.8 9.8 0 0 1-1.5-5.28C2.22 6.48 6.66 2.04 12.12 2.04c2.58 0 5.04 1.02 6.84 2.82a9.63 9.63 0 0 1 2.82 6.84c0 5.46-4.44 9.9-9.66 9.9Zm5.4-7.38c-.3-.18-1.8-.9-2.1-1.02-.3-.12-.48-.18-.72.18-.18.3-.72 1.02-.9 1.2-.18.18-.3.24-.6.06-.3-.18-1.2-.42-2.28-1.38-.84-.72-1.44-1.62-1.62-1.92-.18-.3 0-.42.12-.6.12-.12.3-.3.42-.48.12-.18.18-.3.3-.48.12-.18.06-.36-.06-.54-.12-.18-.72-1.74-.96-2.34-.24-.6-.48-.54-.72-.54h-.6c-.18 0-.48.06-.72.3-.24.24-.9.9-.9 2.16 0 1.26.9 2.46 1.08 2.64.18.18 1.8 2.76 4.38 3.84.6.24 1.08.42 1.44.54.6.18 1.14.18 1.56.12.48-.06 1.5-.6 1.68-1.2.24-.6.24-1.08.18-1.2-.06-.12-.24-.18-.54-.36Z" />
            </svg>
            WhatsApp schreiben
          </Button>
        </div>
      </div>
    </section>
  );
}
